import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { toast } from 'sonner';
import { apiUrlFrom } from '@/app/env';
import { submitPlaytestRequest } from '@/net/lobbyApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

export const Route = createFileRoute('/_site/playtest')({
  head: () => ({
    meta: [
      { title: 'Request a playtest pass | Everwake' },
      {
        name: 'description',
        content:
          'Everwake playtests are supervised and open by one-time pass. Leave your details and a person will get back to you. Adults only, 18+.',
      },
      { property: 'og:title', content: 'Request an Everwake playtest pass' },
      {
        property: 'og:description',
        content:
          'Supervised playtests, one session per pass, every pass issued by a person. 18+ only.',
      },
      { property: 'og:type', content: 'website' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
  }),
  component: Playtest,
});

function Playtest() {
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    headphones: false,
    microphone: false,
    timezone: '',
    heardFrom: '',
    adult: false,
  });

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form.adult) {
      toast.error('You must confirm you are eighteen or older.');
      return;
    }
    const apiUrl = apiUrlFrom();
    if (!apiUrl) {
      toast.error('This site is not connected to a session pod, so the form cannot be sent right now.');
      return;
    }
    setSending(true);
    try {
      await submitPlaytestRequest(apiUrl, {
        name: form.name.trim(),
        email: form.email.trim(),
        timezone: form.timezone.trim(),
        hasHeadphones: form.headphones,
        hasMicrophone: form.microphone,
        ...(form.heardFrom.trim() ? { heardFrom: form.heardFrom.trim() } : {}),
        adultConfirmed: true,
      });
      setDone(true);
    } catch {
      toast.error('That did not go through. Please try again.');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl px-5 py-16">
      <p className="rule-line">Playtest</p>
      <h1 className="mt-4 font-display text-4xl md:text-5xl">Ask for a pass</h1>
      <p className="mt-5 text-muted-foreground">
        Playtests are supervised, sessions open by one-time pass, and every pass is issued by a person. One pass opens exactly one complimentary playtest session.
      </p>

      {done ? (
        <div className="mt-10 rounded-xl border border-primary/40 bg-primary/5 p-6">
          <h2 className="font-display text-2xl">Your request is in.</h2>
          <p className="mt-3 text-sm text-muted-foreground">
            Someone will read it and write back to you. Passes are handed out in small batches, so it may be a little while.
          </p>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="mt-10 space-y-6">
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                required
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="timezone">Timezone</Label>
            <Input
              id="timezone"
              required
              placeholder="UTC+2, or the city you keep time by"
              value={form.timezone}
              onChange={(e) => set('timezone', e.target.value)}
            />
          </div>

          <div className="space-y-3 rounded-lg border border-border bg-card/50 p-4">
            <div className="flex items-center gap-3">
              <Checkbox
                id="headphones"
                checked={form.headphones}
                onCheckedChange={(v) => set('headphones', v === true)}
              />
              <Label htmlFor="headphones" className="font-normal">
                I have headphones
              </Label>
            </div>
            <div className="flex items-center gap-3">
              <Checkbox
                id="microphone"
                checked={form.microphone}
                onCheckedChange={(v) => set('microphone', v === true)}
              />
              <Label htmlFor="microphone" className="font-normal">
                I have a microphone
              </Label>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="heard">How did you hear about it?</Label>
            <Input
              id="heard"
              value={form.heardFrom}
              onChange={(e) => set('heardFrom', e.target.value)}
            />
          </div>

          <div className="flex items-start gap-3 rounded-lg border border-primary/40 bg-primary/5 p-4">
            <Checkbox
              id="adult"
              checked={form.adult}
              onCheckedChange={(v) => set('adult', v === true)}
            />
            <Label htmlFor="adult" className="font-normal leading-relaxed">
              I confirm I am eighteen or older. Everwake is an adults-only game.
            </Label>
          </div>

          <Button type="submit" size="lg" disabled={sending}>
            {sending ? 'Sending…' : 'Request a playtest pass'}
          </Button>
        </form>
      )}
    </div>
  );
}
